import { startOfWeek, startOfMonth, endOfMonth, format } from 'date-fns';
import { Button } from '@/shared/ui/Button';
import { cn } from '@/shared/lib/utils';

export type ViewMode = 'day' | 'week' | 'month';

interface ViewModeSwitcherProps {
  value: ViewMode;
  onChange: (mode: ViewMode, dateFrom: string, dateTo: string, referenceDate?: Date) => void;
}

function computeRange(mode: ViewMode, now: Date = new Date()): { dateFrom: string; dateTo: string; referenceDate: Date } {
  switch (mode) {
    case 'day': {
      const date = format(now, 'yyyy-MM-dd');
      return { dateFrom: date, dateTo: date, referenceDate: now };
    }
    case 'week': {
      const weekStart = startOfWeek(now, { weekStartsOn: 0 });
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      return {
        dateFrom: format(weekStart, 'yyyy-MM-dd'),
        dateTo: format(weekEnd, 'yyyy-MM-dd'),
        referenceDate: weekStart,
      };
    }
    case 'month': {
      const monthStart = startOfMonth(now);
      const monthEnd = endOfMonth(now);
      return {
        dateFrom: format(monthStart, 'yyyy-MM-dd'),
        dateTo: format(monthEnd, 'yyyy-MM-dd'),
        referenceDate: monthStart,
      };
    }
  }
}

export { computeRange };

const MODES: { key: ViewMode; label: string }[] = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
];

export function ViewModeSwitcher({ value, onChange }: ViewModeSwitcherProps) {
  return (
    <div className="flex items-center gap-1 rounded-xl bg-[var(--bg-surface-muted)] p-1">
      {MODES.map((mode) => (
        <Button
          key={mode.key}
          variant="ghost"
          onClick={() => {
            const range = computeRange(mode.key);
            onChange(mode.key, range.dateFrom, range.dateTo, range.referenceDate);
          }}
          className={cn(
            'rounded-lg px-3 py-1.5 text-[12px] font-semibold',
            value === mode.key
              ? 'bg-[var(--bg-surface)] text-[var(--text-primary)] shadow-soft'
              : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
          )}
        >
          {mode.label}
        </Button>
      ))}
    </div>
  );
}
